import { DEFAULT_OG_IMAGE, SITE_NAME, SITE_URL, getCanonicalUrl } from "@/config/site";
import { WebPageSchemaOptions } from "@/types/seo";

export const createOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE_NAME,
  url: SITE_URL,
  logo: DEFAULT_OG_IMAGE,
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "sales",
      email: "contact@helpgenie.ai",
      url: getCanonicalUrl("/contact"),
    },
    {
      "@type": "ContactPoint",
      contactType: "customer support",
      email: "support@helpgenie.ai",
      url: getCanonicalUrl("/contact"),
    },
  ],
});

export const createWebSiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: SITE_URL,
  description:
    "Help Genie helps businesses deploy voice AI agents for websites, phones, lead capture, customer support and after-hours coverage.",
});

export const createBreadcrumbSchema = (items: Array<{ name: string; url: string }>) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: item.url,
  })),
});

export const createServiceSchema = (name: string, description: string, url: string) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name,
  description,
  url,
  serviceType: "Voice AI Automation",
  provider: {
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    logo: DEFAULT_OG_IMAGE,
  },
  areaServed: "Worldwide",
});

export const createProductSchema = (name: string, description: string, url: string) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name,
  description,
  url,
  brand: {
    "@type": "Brand",
    name: SITE_NAME,
  },
  manufacturer: {
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
  },
});

export const createCollectionPageSchema = (name: string, description: string, url: string) => ({
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name,
  description,
  url,
  isPartOf: {
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
  },
});

export const createSoftwareApplicationSchema = (name: string, description: string, url: string) => ({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name,
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  description,
  url,
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "USD",
  },
});

export const createFaqPageSchema = (questions: Array<{ question: string; answer: string }>) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: questions.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.answer,
    },
  })),
});

export const createHowToSchema = (name: string, description: string, steps: Array<{ name: string; text: string }>) => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  name,
  description,
  totalTime: "PT10M",
  step: steps.map((step, index) => ({
    "@type": "HowToStep",
    position: index + 1,
    name: step.name,
    text: step.text,
  })),
});

export const createContactPageSchema = () => ({
  "@context": "https://schema.org",
  "@type": "ContactPage",
  mainEntity: {
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    contactPoint: [
      {
        "@type": "ContactPoint",
        contactType: "sales",
        email: "contact@helpgenie.ai",
      },
      {
        "@type": "ContactPoint",
        contactType: "customer support",
        email: "support@helpgenie.ai",
      },
    ],
  },
});

export const createWebPageSchema = ({
  title,
  description,
  url,
  image = DEFAULT_OG_IMAGE,
  type = "WebPage",
}: WebPageSchemaOptions) => ({
  "@context": "https://schema.org",
  "@type": type,
  name: title,
  description,
  url,
  image,
  isPartOf: {
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
  },
});
